import { useEffect, useMemo, useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, App, Form, Spin } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { MemberFormFields, type MemberFormFieldsValue, type MemberType } from './member-form-fields.tsx'

dayjs.extend(utc)

export type MemberEditorRecord = {
  id: number
  type: MemberType
  name: string
  phone: string | null
  email: string | null
  jobTitle: string | null
  isSalesperson: boolean
  hireDate: Date | string | null
  note: string | null
}

type MemberForm = MemberFormFieldsValue
type MemberEditorModalProps = {
  canManageAgents: boolean
  canManageEmployees: boolean
  editing?: number | 'new'
  previewRecord?: MemberEditorRecord
  onClose: () => void
  onSaved: () => Promise<void>
}

function toDatePickerValue(value: Date | string | null | undefined): Dayjs | undefined {
  if (!value) {
    return undefined
  }
  const date = dayjs.utc(value)
  return date.isValid() ? date.startOf('day') : undefined
}

export function MemberEditorModal(props: MemberEditorModalProps) {
  const [session, setSession] = useState<Pick<MemberEditorModalProps, 'editing' | 'previewRecord'>>()
  if (props.editing !== undefined && props.editing !== session?.editing) {
    setSession({ editing: props.editing, previewRecord: props.previewRecord })
  }

  // 保留当前表单直到退出动画结束，避免关闭时 key 改变导致直接卸载。
  return session ? (
    <MemberEditorModalContent
      {...props}
      {...session}
      key={session.editing}
      open={props.editing !== undefined}
      afterClose={() => setSession(undefined)}
    />
  ) : null
}

function MemberEditorModalContent({
  canManageAgents,
  canManageEmployees,
  editing,
  previewRecord,
  open,
  afterClose,
  onClose,
  onSaved
}: MemberEditorModalProps & { open: boolean; afterClose: () => void }) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<MemberForm>()
  const upsert = useMutation(trpc.member.upsert.mutationOptions())
  const detail = useQuery({
    ...trpc.member.detail.queryOptions(typeof editing === 'number' ? editing : 0),
    enabled: open && typeof editing === 'number',
    refetchOnWindowFocus: false
  })
  const member = detail.data as MemberEditorRecord | null | undefined
  const displayedMember = member ?? previewRecord
  const initialType: MemberType = canManageEmployees ? 'employee' : 'agent'
  const initialValues = useMemo<MemberForm>(
    () =>
      editing !== undefined && editing !== 'new' && displayedMember
        ? {
            type: displayedMember.type,
            name: displayedMember.name,
            phone: displayedMember.phone ?? undefined,
            email: displayedMember.email ?? '',
            jobTitle: displayedMember.jobTitle ?? undefined,
            isSalesperson: displayedMember.isSalesperson,
            hireDate: toDatePickerValue(displayedMember.hireDate),
            note: displayedMember.note ?? undefined
          }
        : {
            type: initialType,
            name: '',
            email: '',
            phone: '',
            jobTitle: '',
            isSalesperson: true,
            hireDate: undefined,
            note: ''
          },
    [editing, initialType, displayedMember]
  )

  useEffect(() => {
    if (!editing) {
      return
    }
    if (editing !== 'new' && !displayedMember) {
      return
    }
    form.resetFields()
    form.setFieldsValue(initialValues)
  }, [editing, form, initialValues, displayedMember])
  const modalTitle = editing === 'new' ? '新建成员' : `编辑 · ${displayedMember?.name ?? '成员'}`
  const detailLoading = typeof editing === 'number' && detail.isPending
  const detailError = typeof editing === 'number' && detail.isError
  const detailUnavailable = typeof editing === 'number' && detail.isFetched && !member

  const save = async (values: MemberForm) => {
    if (editing !== 'new' && !member) {
      return false
    }
    try {
      await upsert.mutateAsync({
        id: editing === 'new' ? undefined : editing,
        data: {
          type: values.type,
          name: values.name.trim(),
          email: values.email?.trim() || null,
          phone: values.phone?.trim() || undefined,
          jobTitle: values.type === 'employee' ? values.jobTitle?.trim() : undefined,
          isSalesperson: values.isSalesperson,
          hireDate:
            values.type === 'employee' && values.hireDate
              ? dayjs.utc(values.hireDate.format('YYYY-MM-DD')).toDate()
              : null,
          note: values.note?.trim() || undefined
        }
      })
      void message.success('成员资料已保存')
      await onSaved()
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '保存失败'))
      return false
    }
  }

  return (
    <ModalForm<MemberForm>
      form={form}
      initialValues={initialValues}
      layout="vertical"
      modalProps={{ afterClose, destroyOnHidden: true, mask: { closable: false }, onCancel: onClose }}
      onFinish={save}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose()
        }
      }}
      open={open}
      scrollToFirstError
      submitter={{
        resetButtonProps: { onClick: onClose },
        searchConfig: { resetText: '取消', submitText: '保存成员' },
        submitButtonProps: {
          disabled: detailLoading || detailError || detailUnavailable || !editing,
          loading: upsert.isPending
        }
      }}
      title={modalTitle}
      width="42rem"
    >
      {detailError ? <Alert showIcon title="成员详情加载失败，请关闭后重试" type="error" /> : null}
      {detailUnavailable && !detailError ? <Alert showIcon title="成员不存在或已被删除" type="error" /> : null}
      {(editing === 'new' || typeof editing === 'number') && (editing === 'new' || displayedMember) ? (
        <Spin spinning={detailLoading}>
          <MemberFormFields
            emailRequired={false}
            disabled={upsert.isPending || detailLoading || detailError || detailUnavailable}
            typeDisabled={editing !== 'new'}
            typeOptions={[
              ...(canManageEmployees ? [{ label: '内部员工', value: 'employee' as const }] : []),
              ...(canManageAgents ? [{ label: '代理商', value: 'agent' as const }] : [])
            ]}
          />
        </Spin>
      ) : null}
    </ModalForm>
  )
}
